import { Chart } from 'react-google-charts';
import AdminLayout from '../../layout/AdminLayout';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useMemo, Fragment } from 'react';

import * as Server from 'assets/js/Server';

export const options = {
  allowHtml: true,
};

export default function IncentiveOrganization(){
  const [orgItemList, setOrgItemList] = useState([]);
  const [searchParams] = useSearchParams();

  const getOrgItemList = () => {
    Server.sendGet('shopify/sales/org', {
      salesPersonId: searchParams.get('salesPersonId'),
      viewType: searchParams.get('viewType'),
      searchMonth: searchParams.get('searchMonth'),

    }, getOrgItemListCallback).then();
  }

  const getOrgItemListCallback = res => {
    setOrgItemList(res?.['resultList'] || []);
  }

  useEffect(() => {
    getOrgItemList();
  }, [searchParams.get('salesPersonId')]);

  // --- 데이터 가공 로직 ---

  // 1. 구글 조직도(OrgChart)용 데이터 변환
  const chartData = useMemo(() => {
    if (!orgItemList || orgItemList.length === 0) return [["Name", "Manager"]];

    const baseData = [["Name", "Manager"]];
    orgItemList.forEach((item) => {
      baseData.push([
        {
          v: item.referralId,
          f: `<div class='node-item'>
                <strong class='node-tit'>${item.salesPersonName}</strong>
                <span class='node-lev'>${item.gid === "0" ? 'Own' : `G${item.gid}`}</span>
                <p class='node-sales'>Sales: ${Number(item.totalSales).toLocaleString()}</p>
              </div>`,
        },
        // 본인(generation 0)은 부모가 없고, 나머지는 up_referral_id를 부모로 연결
        item.gid === 0 ? "" : item.upReferralId
      ]);
    });
    return baseData;
  }, [orgItemList]);

  // 2. 상단 인센티브 요약 데이터 계산 (본인 포함 G1~G5 합계)
  const summary = useMemo(() => {
    const res = {
      selfSales: 0, g1Sales: 0, g2Sales: 0, g3Sales: 0, g4Sales: 0, g5Sales: 0,
      totalSales: 0, estimatedInc: 0
    };

    orgItemList.forEach(item => {
      const sales = Number(item.totalSales || 0);
      res.totalSales += sales;

      if (item.gid === "0") {
        res.selfSales = sales;
        res.estimatedInc += sales * 0.15;

        // 본인 매출이 10만 이상일 경우 별도(Special) 매출 및 인센티브 계산
        if (sales >= 100000) {
          res.specialSales = sales;       // 별도 매출로 잡음
          res.specialInc += sales * 0.05; // 5% 추가 인센티브
          res.estimatedInc += sales * 0.05; // 전체 예상 인센티브에도 합산
        }

      } else if (item.gid === "1") {
        res.g1Sales += sales;
        res.estimatedInc += sales * 0.10;
      } else if (item.gid === "2") {
        res.g2Sales += sales;
        res.estimatedInc += sales * 0.05;
      } else if (item.gid === "3") {
        res.g3Sales += sales;
        res.estimatedInc += sales * 0.03;
      } else if (item.gid === "4") {
        res.g4Sales += sales;
        res.estimatedInc += sales * 0.02;
      } else if (item.gid === "5") {
        res.g5Sales += sales;
        res.estimatedInc += sales * 0.01;
      }
    });

    return res;
  }, [orgItemList]);

  // 인센티브 비율 반환 함수 (테이블용)
  const getRate = (gen) => {
    const rates = { 0: '15%', 1: '10%', 2: '5%', 3: '3%', 4: '2%', 5: '1%' };
    return rates[gen] || '0%';
  };

  return (
    <AdminLayout>
      <div className='title'>
        <div className='tit-head'>
          <i />
          <h4>Organization Chart(Incentive)</h4>
        </div>
      </div>
      <div className="content none-util">
        <div className="org-top">
          <div className="incentive-wrap">
            <div className="input-in">
              <label>Personal Sales</label>
              <input type="text" readOnly value={`Personal Sales: ${Number(summary.selfSales).toLocaleString()} INR`} />
            </div>
            <div className="input-in">
              <label>Estimated Commission (PS, 15%)</label>
              <input type="text" readOnly value={`Commission (PS, 15%): ${Number(summary.selfSales * 0.15).toLocaleString()} INR`} />
            </div>

            <div className="input-in">
              <label>G1 Sales</label>
              <input type="text" readOnly value={`G1 Sales: ${Number(summary.g1Sales).toLocaleString()} INR`} />
            </div>
            <div className="input-in">
              <label>Estimated Commission(G1, 10%)</label>
              <input type="text" readOnly value={`Commission(G1, 10%): ${Number(summary.g1Sales * 0.1).toLocaleString()} INR`} />
            </div>

            <div className="input-in">
              <label>G2 Sales</label>
              <input type="text" readOnly value={`G2 Sales: ${Number(summary.g2Sales).toLocaleString()} INR`} />
            </div>
            <div className="input-in">
              <label>Estimated Commission(G2, 5%)</label>
              <input type="text" readOnly value={`Commission(G2, 5%): ${Number(summary.g2Sales * 0.05).toLocaleString()} INR`} />
            </div>

            <div className="input-in">
              <label>G3 Sales</label>
              <input type="text" readOnly value={`G3 Sales: ${Number(summary.g3Sales).toLocaleString()} INR`} />
            </div>
            <div className="input-in">
              <label>Estimated Commission(G3, 3%)</label>
              <input type="text" readOnly value={`Commission(G3, 3%): ${Number(summary.g3Sales * 0.03).toLocaleString()} INR`} />
            </div>

            <div className="input-in">
              <label>G4 Sales</label>
              <input type="text" readOnly value={`G4 Sales: ${Number(summary.g4Sales).toLocaleString()} INR`} />
            </div>
            <div className="input-in">
              <label>Estimated Commission(G4, 2%)</label>
              <input type="text" readOnly value={`Commission(G4, 2%): ${Number(summary.g4Sales * 0.02).toLocaleString()} INR`} />
            </div>

            <div className="input-in">
              <label>G5 Sales</label>
              <input type="text" readOnly value={`G5 Sales: ${Number(summary.g5Sales).toLocaleString()} INR`} />
            </div>
            <div className="input-in">
              <label>Estimated Commission(G5, 1%)</label>
              <input type="text" readOnly value={`Commission(G5, 1%): ${Number(summary.g5Sales * 0.01).toLocaleString()} INR`} />
            </div>

            <div className="input-in">
              <label>Special</label>
              <input type="text" readOnly value='Special' />
            </div>
            <div className="input-in">
              <label>Special(5%)</label>
              <input
                type="text"
                readOnly
                value={`Special(5%): ${Number((summary?.specialSales || 0) * 0.05).toLocaleString()} INR`}
              />
            </div>

            <div className="input-in">
              <label>Total Sales</label>
              <input type="text" readOnly value={`Total Sales: ${Number(summary.totalSales).toLocaleString()} INR`} />
            </div>
            <div className="input-in">
              <label>Estimated Commission(Total)</label>
              <input
                type="text"
                readOnly
                value={`Commission(Total): ${Number(summary.estimatedInc).toLocaleString()} INR`}
                style={{color: '#2F80ED', fontWeight: 'bold'}}
              />
            </div>
          </div>
          <div className="chart-wrap" style={{ overflow: 'auto' }}>
            <Chart
              chartType="OrgChart"
              data={chartData}
              options={options}
              width="100%"
              height="400px"
            />
          </div>
        </div>
        <div className="org-bot">
          <table className="org-table">
            <thead>
            <tr>
              <th rowSpan={2}>G</th>
              <th rowSpan={2}>Member Name</th>
              <th rowSpan={2}>Total</th>
              <th colSpan={2}>Incentive</th>
            </tr>
            <tr>
              <th>Rate</th>
              <th>Amount</th>
            </tr>
            </thead>
            <tbody>
            {orgItemList.map((item, idx) => {
              const baseRate = parseFloat(getRate(item.gid));
              const rateVal = baseRate / 100;

              // 본인(gid '0')이면서 매출이 10만 이상인 경우 Special 행 표시 여부
              const isSpecial = item.gid === '0' && Number(item.totalSales) >= 100000;

              return (
                <Fragment key={idx}>
                  {/* 기본 인센티브 행 */}
                  <tr>
                    <td>{item.gid === '0' ? 'Own' : `G${item.gid}`}</td>
                    <td>{item.salesPersonName}</td>
                    <td className="right">{Number(item.totalSales).toLocaleString()}</td>
                    <td>{baseRate}%</td>
                    <td className="right">{(item.totalSales * rateVal).toLocaleString()}</td>
                  </tr>

                  {/* 10만 이상일 때 추가되는 Special 행 (0.5%) */}
                  {isSpecial && (
                    <tr className="special-row" style={{ background: '#fef9c3' }}>
                      <td></td>
                      <td style={{ color: '#eab308', fontWeight: 'bold'}}>Own (Special)</td>
                      <td></td>
                      <td>5%</td>
                      <td className="right">{(item.totalSales * 0.05).toLocaleString()}</td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  )
}
